import { Heart, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface FigmaPartCardProps {
  id: string
  title: string
  specText?: string
  price: string
  originalPrice?: string
  image: string
  isOem?: boolean
  isWishlisted?: boolean
  onSelect?: (id: string) => void
  onToggleWishlist?: (id: string) => void
  className?: string
}

export function FigmaPartCard({
  id,
  title,
  specText = '2024 · Brand New · xDrive',
  price,
  originalPrice = '$450.00',
  image,
  isOem = true,
  isWishlisted = false,
  onSelect,
  onToggleWishlist,
  className,
}: FigmaPartCardProps) {
  return (
    <div
      onClick={() => onSelect?.(id)}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-[20px] bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-xl cursor-pointer p-4',
        className
      )}
    >
      {/* Top badges & Heart */}
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-white flex items-center justify-center">
        {/* OEM Red Pill Badge */}
        {isOem && (
          <span
            style={{
              backgroundColor: '#CF2F2F',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '10px',
              fontWeight: 800,
            }}
            className="absolute top-2 left-2 z-10 rounded-md px-2 py-0.5 text-white uppercase tracking-wider"
          >
            OEM
          </span>
        )}

        {/* Wishlist Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onToggleWishlist?.(id)
          }}
          className="absolute top-2 right-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/80 border border-gray-100 shadow-xs text-gray-400 hover:text-red-500 transition-all"
        >
          <Heart className={cn('h-3.5 w-3.5', isWishlisted && 'fill-red-500 text-red-500')} />
        </button>

        {/* Product Image */}
        <img
          src={image}
          alt={title}
          className="h-full w-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col pt-3">
        {/* 5-star rating */}
        <div className="flex items-center gap-0.5 mb-1 text-amber-400 text-xs">
          {'★'.repeat(5)}
        </div>

        {/* Title */}
        <h3
          style={{ fontFamily: "'DM Sans', sans-serif" }}
          className="font-bold text-sm text-gray-900 line-clamp-1"
        >
          {title}
        </h3>

        {/* Spec Subtitle */}
        <p
          style={{ fontFamily: "'DM Sans', sans-serif" }}
          className="text-[11px] text-gray-400 mt-0.5 mb-2"
        >
          {specText}
        </p>

        {/* Price Row */}
        <div className="flex items-baseline gap-2 mb-3">
          <span
            style={{ color: '#CF2F2F', fontFamily: "'DM Sans', sans-serif" }}
            className="font-extrabold text-base"
          >
            {price}
          </span>
          {originalPrice && (
            <span
              style={{ fontFamily: "'DM Sans', sans-serif" }}
              className="text-xs text-gray-400 line-through"
            >
              {originalPrice}
            </span>
          )}
        </div>

        {/* VIEW DETAILS Red Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onSelect?.(id)
          }}
          style={{
            backgroundColor: '#CF2F2F',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.05em',
          }}
          className="w-full py-2.5 rounded-xl text-white uppercase inline-flex items-center justify-center gap-1.5 shadow-sm transition-all hover:opacity-95 active:scale-98 mt-auto"
        >
          <ArrowRight className="h-3.5 w-3.5" />
          <span>VIEW DETAILS</span>
        </button>
      </div>
    </div>
  )
}
